import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  AuditTrailEntryKind,
  instrumentVersionLabel,
  isAssessmentActivityKind,
  type ActivityEntry,
  type Assessment,
} from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import {
  ACTIVITY_LOG_GROWTH,
  useGrowingLimit,
} from "../../hooks/useGrowingLimit.js";
import {
  ActivityLog,
  type DescribedEntry,
  type Translate,
} from "../ActivityLog/ActivityLog.js";

/** Wording for a definitive assessment, a contributing one, or neither known. */
const byDefinitive = (
  definitive: boolean | undefined,
  wording: { definitive: string; contributing: string; unstated: string },
) =>
  definitive === undefined
    ? wording.unstated
    : definitive
      ? wording.definitive
      : wording.contributing;

/**
 * What an assessment entry says beyond the act's own name, or `null` where
 * there is nothing more to say than that.
 *
 * Each payload is read field by field, so an entry recorded by a build that
 * wrote something else still says what happened once a caller falls back to
 * the act. Whether the assessment is definitive is fixed for its life, so the
 * assessment's own flag stands in for an entry recorded before it was stated.
 */
export const describeAssessmentAct = (
  entry: ActivityEntry,
  t: Translate,
  assessmentIsDefinitive?: boolean,
): string | null => {
  if (!isAssessmentActivityKind(entry.kind)) {
    return null;
  }

  const { submissionNumber } = entry.data;
  const instrument =
    typeof entry.data["instrument"] === "string"
      ? instrumentVersionLabel(entry.data["instrument"])
      : undefined;
  const definitive =
    typeof entry.data["definitive"] === "boolean"
      ? entry.data["definitive"]
      : assessmentIsDefinitive;

  // Names the version where the entry records it; the start always names it,
  // in its own wording.
  const against = (said: string) =>
    instrument !== undefined
      ? t("personPage.assessments.activity.againstVersion", {
          act: said,
          instrument,
        })
      : said;

  return match(entry.kind)
    .with(AuditTrailEntryKind.assessment_started, () =>
      instrument !== undefined
        ? t(
            byDefinitive(definitive, {
              definitive: "personPage.assessments.activity.startedDefinitive",
              contributing:
                "personPage.assessments.activity.startedContributing",
              unstated: "personPage.assessments.activity.startedAgainst",
            }),
            { instrument },
          )
        : null,
    )
    .with(AuditTrailEntryKind.assessment_submitted, () =>
      typeof submissionNumber === "number"
        ? against(
            t(
              byDefinitive(definitive, {
                definitive:
                  "personPage.assessments.activity.submittedForApproval",
                contributing:
                  "personPage.assessments.activity.submittedCompleting",
                unstated: "personPage.assessments.activity.submittedAttempt",
              }),
              { number: submissionNumber },
            ),
          )
        : null,
    )
    .with(AuditTrailEntryKind.assessment_approved, () =>
      typeof submissionNumber === "number"
        ? against(
            t("personPage.assessments.activity.approvedAttempt", {
              number: submissionNumber,
            }),
          )
        : null,
    )
    .with(AuditTrailEntryKind.assessment_returned, () =>
      typeof submissionNumber === "number"
        ? against(
            t("personPage.assessments.activity.returnedAttempt", {
              number: submissionNumber,
            }),
          )
        : null,
    )
    .with(AuditTrailEntryKind.assessment_reopened, () =>
      typeof submissionNumber === "number"
        ? against(
            t("personPage.assessments.activity.reopenedFrom", {
              number: submissionNumber,
            }),
          )
        : null,
    )
    .with(AuditTrailEntryKind.assessment_draft_started, () =>
      typeof entry.data["fromSubmissionNumber"] === "number"
        ? t("personPage.assessments.activity.draftFrom", {
            number: entry.data["fromSubmissionNumber"],
          })
        : null,
    )
    .with(
      AuditTrailEntryKind.assessment_deleted,
      AuditTrailEntryKind.assessment_undeleted,
      AuditTrailEntryKind.assessment_pdf_downloaded,
      AuditTrailEntryKind.guest_assessments_shared,
      AuditTrailEntryKind.shared_assessment_read,
      () => null,
    )
    .exhaustive();
};

/** What one entry says about the assessment: its detail, or the act alone. */
export const describeAssessmentEntry = (
  entry: ActivityEntry,
  t: Translate,
  assessmentIsDefinitive?: boolean,
): DescribedEntry => ({
  subject:
    describeAssessmentAct(entry, t, assessmentIsDefinitive) ??
    t(`personPage.assessments.activity.event.${entry.kind}`, {
      defaultValue: entry.kind,
    }),
  change: null,
});

export type AssessmentActivityContentProps = {
  instrument: Assessment["instrument"];
  definitive: Assessment["definitive"];
  entries: readonly ActivityEntry[];
  count: number;
  loading: boolean;
  errored: boolean;
  onShowMore?: () => Promisable<void>;
};

/**
 * The log itself, given everything it renders, so it can be shown in any state
 * without a server.
 */
export const AssessmentActivityContent = ({
  instrument,
  definitive,
  entries,
  count,
  loading,
  errored,
  onShowMore,
}: AssessmentActivityContentProps) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2" component="h3">
        {t("personPage.assessments.activity.title")}
      </Typography>

      {/* Said above the entries because the entry that started the assessment
          is the last one a newest-first log shows, and may not be loaded yet. */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ overflowWrap: "anywhere" }}
      >
        {t("personPage.assessments.activity.version", {
          instrument: instrumentVersionLabel(instrument.name),
        })}
      </Typography>

      <ActivityLog
        entries={entries}
        count={count}
        loading={loading}
        errored={errored}
        empty={t("personPage.assessments.activity.empty")}
        describe={(entry, translate) =>
          describeAssessmentEntry(entry, translate, definitive)
        }
        onShowMore={onShowMore}
      />
    </Stack>
  );
};

export const AssessmentActivity = ({
  assessment,
}: {
  assessment: Assessment;
}) => {
  const { limit, onShowMore } = useGrowingLimit(ACTIVITY_LOG_GROWTH);

  const [{ data, loading, error }] = apiHooks.assessmentActivity.forAssessment(
    assessment.id,
    {
      limit,
      offset: 0,
    },
  );

  return (
    <AssessmentActivityContent
      instrument={assessment.instrument}
      definitive={assessment.definitive}
      entries={data?.entries ?? []}
      count={data?.count ?? 0}
      loading={loading}
      errored={error !== undefined && error !== null}
      onShowMore={onShowMore}
    />
  );
};
